import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const AnimatedBackground: React.FC<React.PropsWithChildren> = ({ children }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let width = window.innerWidth;
    let height = window.innerHeight;
    
    const particles: Particle[] = [];
    const particleCount = 25;
    const colors = [
      'rgba(99, 102, 241, 0.7)',   // 紫蓝色
      'rgba(79, 70, 229, 0.7)',    // 靛蓝色
      'rgba(129, 140, 248, 0.7)',  // 亮紫色
      'rgba(55, 48, 163, 0.7)',    // 深蓝色
      'rgba(139, 92, 246, 0.7)',   // 淡紫色
    ];
    
    // 粒子类
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.size = Math.random() * 200 + 100;
        this.speedX = Math.random() * 1 - 0.5;
        this.speedY = Math.random() * 1 - 0.5;
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x > width) this.x = 0 - this.size;
        else if (this.x + this.size < 0) this.x = width;
        
        if (this.y > height) this.y = 0 - this.size;
        else if (this.y + this.size < 0) this.y = height;
      }
      
      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // 创建粒子
    const init = () => {
      canvas.width = width;
      canvas.height = height;
      
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    }
    
    // 动画循环
    const animate = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      
      ctx.fillStyle = 'rgb(17, 24, 39)';
      ctx.fillRect(0, 0, width, height);
      
      // 使用全局合成操作让颜色混合
      ctx.globalCompositeOperation = 'lighter';
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      ctx.globalCompositeOperation = 'source-over';
      
      requestAnimationFrame(animate);
    }
    
    // 处理窗口大小调整
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }
    
    window.addEventListener('resize', handleResize);
    
    init();
    animate();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  return (
    <Wrapper>
      <Canvas ref={canvasRef} />
      <Content>{children}</Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
`;

export default AnimatedBackground; 